import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { signupForEarlyAccess } from '@/services/api';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid work email address'),
});

type EmailFormData = z.infer<typeof emailSchema>;

export default function EmailSignup() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  const onSubmit = async (data: EmailFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await signupForEarlyAccess(data.email);
      setSubmitted(true);
      reset();

      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-primary-600 dark:bg-primary-700">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to see Notematic in your service?
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Book a demo or request a guided pilot for your centre, service, or network.
          </p>

          {submitted ? (
            <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 animate-fade-in">
              <div className="flex items-center justify-center text-success-600 dark:text-success-400 mb-2">
                <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">Thank you — we'll be in touch shortly.</span>
              </div>
              <p className="text-secondary-600 dark:text-secondary-400">
                Please check your email to confirm your enquiry.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <div className="flex-1">
                  <Input
                    {...register('email')}
                    id="email"
                    type="email"
                    placeholder="Work email"
                    error={errors.email?.message}
                    className="bg-white dark:bg-secondary-800"
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type="submit"
                  variant="secondary"
                  size="md"
                  disabled={isSubmitting}
                  className="sm:w-auto whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>
              </form>
            </>
          )}

          <p className="text-sm text-primary-100 mt-4">
            Privacy-first enquiry handling. No spam.
          </p>
        </div>
      </div>
    </section>
  );
}

